import { useState, useMemo, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { centsToDisplay, inputValueToCents } from '../../lib/money';
import {
  computeAccountCampaignStatus,
  statusPillClass,
  type RowForStatus,
} from '../../lib/accountCampaignStatus';
import { isBudgetModified } from '../../lib/dailyBudget';
import { isCampaignVisibleToLocation } from '../../lib/campaignVisibility';
import {
  campaignIncludesChannel,
  effectiveRunsGoogle,
  effectiveRunsMeta,
} from '../../lib/platformEnabled';
import { resolveGroupTag as resolveGroupTagShared } from '../../lib/groupTag';
import { resolveLeadForm } from '../../lib/resolveLeadForm';
import { IncreaseInput, MonthlyBudgetInput } from './budgetInputs';
import { CampaignCellDropdown } from './CampaignCellDropdown';
import { GroupBadge } from './GroupBadge';
import { LeadFormCell } from './LeadFormCell';
import { formatUserDisplayName } from '../../lib/formatUserDisplayName';
import type { Account, Currency } from '../../types/account';
import type { Campaign } from '../../types/campaign';
import type { CurrentUser } from '../../types/user';
import type { FranchiseLocationProfile } from '../../types/franchiseLocationProfile';
import type {
  AccountCampaign,
  AccountCampaignInlinePatch,
  AccountCampaignStatus,
} from '../../types/accountCampaign';
import type { Rollout } from '../../types/rollout';
import { DailyBudgetCell } from './DailyBudgetCell';
import { DatePickerCell } from './DatePickerCell';
import { BudgetDriftModal } from './BudgetDriftModal';
import { GroupCogMenu } from './GroupCogMenu';
import { NotesCell } from './NotesCell';
import { DuplicateRowModal } from './DuplicateRowModal';
import { OptOutConfirmModal } from './OptOutConfirmModal';
import { RowHistoryModal } from './RowHistoryModal';
import { GeneratePreviewModal } from '../preview';
import {
  RowActionsMenu,
  deriveBrandOptOutStatus,
} from './RowActionsMenu';
import {
  hasEndDateDrift,
  hasRowModifications,
  hasStartDateDrift,
} from './hasRowModifications';
import { ApiError } from '../../lib/api';
import {
  createRowAssetFolders,
  deleteSecondaryRow,
  duplicateRow,
  markRowAsOptOut,
  patchRow,
  patchRowBudget,
  reverseRowOptOut,
} from './api';
import { type Filters, ME, NO_GROUP, UNASSIGNED_CAMPAIGN } from './FilterBar';
import { matchesAssetPresence, matchesBuildStatus } from '../../lib/assetBuildFilters';
import {
  FACTORY_ORDER,
  ALL_COLUMN_KEYS,
  buildHeader,
  cellShadeClass,
  shouldRenderMetaLocalBuiltCell,
  shouldRenderMetaOrganicBuiltCell,
  type ColumnKey,
} from './columns';

type Props = {
  rollout: Rollout;
  rows: AccountCampaign[];
  accounts: Account[];
  locationProfiles: FranchiseLocationProfile[];
  campaigns: Campaign[];
  users: CurrentUser[];
  currentUser: CurrentUser;
  filters: Filters;
  selectedIds: Set<string>;
  onToggleSelect: (id: string, next: boolean) => void;
  // Item 2: the grid owns the filter (visibleRows), so it passes the
  // filtered row ids up — Select All must affect only what's on screen,
  // never every row in the rollout.
  onToggleAll: (next: boolean, visibleIds: string[]) => void;
  // Column ordering + visibility driven by the caller (RolloutDetail manages
  // the preset state and persistence). Optional -- defaults to factory order
  // with every column visible.
  columnOrder?: readonly ColumnKey[];
  visibleColumns?: ReadonlySet<ColumnKey>;
  // Visibility mutator. Supplied by RolloutDetail so the per-group cog menu
  // can toggle columns. Column ORDER is no longer mutated through the grid --
  // group reorder goes through GroupReorderModal which talks directly to the
  // parent's setColumnOrder. When undefined, the cog menu is hidden (safe
  // default for tests / standalone use).
  onVisibleColumnsChange?: (next: Set<ColumnKey>) => void;
  // OrganicAssets spec §3.2 / §6.3: whether the brand's Drive parent folder is
  // configured. Gates the per-row "Create Meta Asset Folder" action so the AM
  // isn't offered an action that would 400. Defaults false (action disabled
  // with an explanatory tooltip) for safety in tests / standalone use.
  brandDriveConfigured?: boolean;
};

type GroupTag = { name: string | null };

// Shared gate used by both the pencil icon in the Campaign cell and the
// "Edit Campaign" entry in the Actions menu. Spec §7.1.2: unassigned and
// opt-out rows have no editable content. Keeping this in one place means
// the two entry points can never disagree on when the editor is reachable.
function rowEditDisabledReason(row: AccountCampaign): string | null {
  if (row.rowType === 'unassigned') return 'Assign a campaign first';
  if (row.rowType === 'opt_out') return 'Opt-out rows have no editable content';
  return null;
}

// Pre-P2b-3 polish round 5 follow-up: per-row Meta ad type cell on
// the master grid. Compact equivalent of the row editor's
// MetaAdTypeControl widget — same underlying decision tree, different
// visual treatment optimised for column scanning.
//
// Render modes:
//   Campaign 'traffic' / 'lead_gen': read-only label "Website Lead" or
//     "Meta Lead Gen", `title="Set on Campaign"`. No interaction. Same
//     locked semantics effectiveMetaAdType short-circuits with.
//   Campaign 'both': native <select> with three options:
//     - 'inherit' — option label is dynamic ("Inherit (Website Lead)" /
//       "Inherit (Meta Lead Gen)" / "Inherit (Pick…)" depending on the
//       location's defaultMetaAdType). Native trigger shows the
//       selected option's label, so AMs scanning the column see
//       the resolved type at a glance.
//     - 'traffic' / 'lead_gen' — explicit row override.
//   "Must pick" highlight: when the row is on inherit AND the
//     location has no default, the select gains amber-50 / amber-300
//     styling so the cell stands out. The dropdown is still
//     interactive — the AM can pick directly without leaving the grid.
//
// Click-and-go: change fires the inline-patch endpoint. Same path
// the row editor's MetaAdTypeControl uses; same server-side schema
// (rowInlinePatchSchema accepts metaAdType).
//
// disabled covers archived rollouts AND platform-off rows (per
// META_PLATFORM_COLUMNS). Opt-out rows render '—' upstream via
// OPT_OUT_VISIBLE_COLUMNS.
function MetaAdTypeCell({
  campaignMetaAdType,
  rowOverride,
  locationDefault,
  busy,
  disabled,
  onChange,
}: {
  campaignMetaAdType: 'traffic' | 'lead_gen' | 'both';
  rowOverride: 'traffic' | 'lead_gen' | null | undefined;
  locationDefault: 'traffic' | 'lead_gen' | null | undefined;
  busy: boolean;
  disabled: boolean;
  onChange: (next: 'traffic' | 'lead_gen' | null) => void;
}) {
  if (campaignMetaAdType !== 'both') {
    const label = campaignMetaAdType === 'traffic' ? 'Website Lead' : 'Meta Lead Gen';
    return (
      <span
        className="text-xs text-slate-500"
        title="Set on Campaign"
      >
        {label}
      </span>
    );
  }
  const value: 'inherit' | 'traffic' | 'lead_gen' =
    rowOverride === 'traffic'
      ? 'traffic'
      : rowOverride === 'lead_gen'
        ? 'lead_gen'
        : 'inherit';
  const inheritLabel =
    locationDefault === 'traffic'
      ? 'Inherit (Website Lead)'
      : locationDefault === 'lead_gen'
        ? 'Inherit (Meta Lead Gen)'
        : 'Inherit (Pick…)';
  const mustPick = value === 'inherit' && (locationDefault ?? null) === null;
  return (
    <select
      className={`w-full rounded border px-1 py-0.5 text-xs disabled:opacity-50 ${
        mustPick ? 'border-amber-300 bg-amber-50' : 'border-slate-300'
      }`}
      value={value}
      disabled={disabled || busy}
      onChange={(e) => {
        const next = e.target.value;
        onChange(
          next === 'traffic' ? 'traffic' : next === 'lead_gen' ? 'lead_gen' : null,
        );
      }}
      title={
        mustPick
          ? 'Pick an ad type, or set a default on this location.'
          : undefined
      }
    >
      <option value="inherit">{inheritLabel}</option>
      <option value="traffic">Website Lead</option>
      <option value="lead_gen">Meta Lead Gen</option>
    </select>
  );
}

function resolveGroupTagForRow(
  row: AccountCampaign,
  accountsById: Map<string, Account>,
): GroupTag {
  // Round-1 polish Fix 5: the underlying walk-up logic moved to
  // src/lib/groupTag.ts so the role-grid filters can reuse it.
  // This module-private wrapper preserves the GroupTag shape used by
  // MasterGrid's existing call sites (just the name).
  const tag = resolveGroupTagShared(row.accountId, accountsById);
  return { name: tag?.name ?? null };
}

function statusFilterMatches(status: AccountCampaignStatus, filter: Filters['status']): boolean {
  if (filter === 'all') return true;
  if (filter === 'drafting') return status === 'Drafting';
  if (filter === 'ready') return status === 'Meta Ready' || status === 'Google Ready' || status === 'Both Ready';
  if (filter === 'building') return status === 'Meta Built' || status === 'Google Built';
  if (filter === 'live') return status === 'Meta Live' || status === 'Google Live';
  if (filter === 'complete') return status === 'Fully Complete';
  if (filter === 'cancelled') return status === 'Cancelled Location';
  return true;
}

export function MasterGrid({
  rollout,
  rows,
  accounts,
  locationProfiles,
  campaigns,
  users,
  currentUser,
  filters,
  selectedIds,
  onToggleSelect,
  onToggleAll,
  columnOrder,
  visibleColumns,
  onVisibleColumnsChange,
  brandDriveConfigured = false,
}: Props) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const archived = rollout.status === 'archived';

  const accountsById = useMemo(() => new Map(accounts.map((a) => [a.id, a])), [accounts]);
  const campaignsById = useMemo(() => new Map(campaigns.map((c) => [c.id, c])), [campaigns]);
  const usersById = useMemo(() => new Map(users.map((u) => [u.uid, u])), [users]);
  const locationProfileById = useMemo(
    () => new Map(locationProfiles.map((p) => [p.accountId, p])),
    [locationProfiles],
  );

  const effectiveOrder = columnOrder ?? FACTORY_ORDER;
  const effectiveVisible = visibleColumns ?? ALL_COLUMN_KEYS;
  const header = useMemo(
    () => buildHeader(effectiveOrder, effectiveVisible),
    [effectiveOrder, effectiveVisible],
  );

  const patchMut = useMutation({
    mutationFn: ({ rowId, patch }: { rowId: string; patch: AccountCampaignInlinePatch }) =>
      patchRow(rollout.id, rowId, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rollout', rollout.id, 'rows'] }),
  });

  const budgetMut = useMutation({
    mutationFn: ({
      rowId,
      platform,
      monthlyCents,
      patchAccount,
    }: {
      rowId: string;
      platform: 'meta' | 'google';
      monthlyCents: number | null;
      patchAccount: boolean;
    }) => patchRowBudget(rollout.id, rowId, { platform, monthlyCents, patchAccount }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rollout', rollout.id, 'rows'] });
      qc.invalidateQueries({ queryKey: ['accounts'] });
    },
  });

  const [drift, setDrift] = useState<null | {
    rowId: string;
    platform: 'meta' | 'google';
    fromCents: number | null;
    toCents: number | null;
    locationName: string;
    currency: Currency;
  }>(null);

  // Opt-out modal state. Pending row is captured so the modal can show the
  // right location name even if the underlying row list refreshes while the
  // modal is open. `busy` locks the confirm button during the server call to
  // prevent double-submit.
  const [optOutTarget, setOptOutTarget] = useState<null | {
    rowId: string;
    locationName: string;
  }>(null);

  // Duplicate-row confirmation state. Captured at click time so the modal
  // shows the right location label even if the underlying row list refreshes
  // while the modal is open.
  const [duplicateTarget, setDuplicateTarget] = useState<null | {
    rowId: string;
    locationName: string;
  }>(null);

  // History-modal state. Chunk C commit 5 mounts the RowHistoryModal here;
  // the menu item is already wired, so we keep the target state but render
  // nothing until the component lands.
  const [historyTarget, setHistoryTarget] = useState<null | {
    rowId: string;
    locationName: string;
  }>(null);

  // Ad Preview Mockups Stage 1 §3.1: Generate Preview modal state.
  // Existing link id (when set) lets the modal pre-fetch the row's
  // current latest preview link so the AM sees the live URL on open
  // rather than the "no link yet" empty state.
  const [previewTarget, setPreviewTarget] = useState<null | {
    rowId: string;
    locationName: string;
    existingLinkId: string | null;
  }>(null);

  const optOutMut = useMutation({
    mutationFn: (rowId: string) => markRowAsOptOut(rollout.id, rowId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rollout', rollout.id, 'rows'] }),
  });
  const reverseOptOutMut = useMutation({
    mutationFn: (rowId: string) => reverseRowOptOut(rollout.id, rowId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rollout', rollout.id, 'rows'] }),
  });
  const deleteSecondaryMut = useMutation({
    mutationFn: (rowId: string) => deleteSecondaryRow(rollout.id, rowId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rollout', rollout.id, 'rows'] }),
  });
  const duplicateMut = useMutation({
    mutationFn: (rowId: string) => duplicateRow(rollout.id, rowId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rollout', rollout.id, 'rows'] }),
  });
  // OrganicAssets spec §3.2: per-row folder creation. Quick action — no
  // confirm (additive + idempotent). Errors (both folders exist, brand not
  // configured) are pre-gated in the menu; an unexpected server error surfaces
  // via alert, consistent with the grid's other inline prompts.
  const createRowFoldersMut = useMutation({
    mutationFn: (rowId: string) => createRowAssetFolders(rollout.id, rowId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rollout', rollout.id, 'rows'] }),
    onError: (err) =>
      window.alert(err instanceof ApiError ? err.message : 'Folder creation failed.'),
  });

  // Brand-level Opt Out Campaign gating (spec §6.7.6). Derived once from the
  // campaigns list the parent component already fetched -- no extra request.
  const brandOptOut = useMemo(
    () => deriveBrandOptOutStatus(rollout.brandAccountId, campaigns),
    [rollout.brandAccountId, campaigns],
  );

  // Filter rows. Server already returns them in the spec §7.1 canonical sort
  // (group ASC, location ASC, primary DESC, "(no group)" last), so we only
  // filter -- we do NOT re-sort here.
  const visibleRows = useMemo(() => {
    const q = filters.text.trim().toLowerCase();
    const amSet = new Set(filters.am.map((v) => (v === ME ? currentUser.uid : v)));
    const campaignSet = new Set(filters.campaignIds);
    const groupSet = new Set(filters.groups);
    const out: AccountCampaign[] = [];
    for (const r of rows) {
      const acc = accountsById.get(r.accountId);
      if (!acc) continue;
      const status = computeAccountCampaignStatus(r as RowForStatus, acc.status);
      if (!statusFilterMatches(status, filters.status)) continue;
      if (amSet.size > 0) {
        if (!r.assignedAmId || !amSet.has(r.assignedAmId)) continue;
      }
      if (campaignSet.size > 0) {
        const key = r.rowType === 'unassigned' ? UNASSIGNED_CAMPAIGN : r.campaignId ?? '';
        if (!campaignSet.has(key)) continue;
      }
      if (groupSet.size > 0) {
        const group = resolveGroupTagForRow(r, accountsById).name;
        const key = group === null ? NO_GROUP : group;
        if (!groupSet.has(key)) continue;
      }
      if (q) {
        const group = resolveGroupTagForRow(r, accountsById).name ?? '';
        const hay = `${acc.name} ${group}`.toLowerCase();
        if (!hay.includes(q)) continue;
      }
      // Multi Lead Form spec §3.3. Orphan-only filter checks resolveLeadForm
      // for each row against its assigned Campaign and includes only the
      // rows where state === 'orphan'.
      if (filters.leadFormOrphan) {
        const campaign = r.campaignId ? campaignsById.get(r.campaignId) ?? null : null;
        if (resolveLeadForm(r, campaign).state !== 'orphan') continue;
      }
      // Organic Assets filter: folder presence (Local / Organic).
      if (!matchesAssetPresence(r, filters.assetPresence)) continue;
      // Build status: Main / Local / Organic "not built" (checkbox state
      // only — independent of file presence).
      if (!matchesBuildStatus(r, filters.buildStatus)) continue;
      out.push(r);
    }
    return out;
  }, [rows, accountsById, campaignsById, filters, currentUser]);

  const allSelected = visibleRows.length > 0 && visibleRows.every((r) => selectedIds.has(r.id));

  const patchField = (rowId: string, patch: AccountCampaignInlinePatch) => {
    if (archived) return;
    patchMut.mutate({ rowId, patch });
  };

  const patchFieldAsync = async (rowId: string, patch: AccountCampaignInlinePatch) => {
    if (archived) return;
    await patchMut.mutateAsync({ rowId, patch });
  };

  // Budget Monthly commit handler. The shared MonthlyBudgetInput parses the
  // raw text into cents-or-null + applies the no-op guard before calling
  // this; here we own the surface-specific drift-vs-direct decision.
  // Drift logic only applies to PRIMARY rows. The Account retainer maps
  // 1:1 to the primary row's Monthly; secondaries are AM-distributed
  // splits of that retainer (or top-ups via Increase) — there is no
  // "row Monthly = Account retainer" invariant to defend, so no modal.
  const onMonthlyCommit = (
    row: AccountCampaign,
    platform: 'meta' | 'google',
    nextCents: number | null,
  ) => {
    if (archived) return;
    const acc = accountsById.get(row.accountId);
    if (!acc) return;
    const currentRowCents = platform === 'meta' ? row.metaMonthlyBudget : row.googleMonthlyBudget;
    if (!row.primary) {
      budgetMut.mutate({ rowId: row.id, platform, monthlyCents: nextCents, patchAccount: false });
      return;
    }
    const accountCents =
      platform === 'meta' ? acc.metaMasterMonthlyBudget : acc.googleMasterMonthlyBudget;
    if (nextCents !== accountCents) {
      setDrift({
        rowId: row.id,
        platform,
        fromCents: currentRowCents,
        toCents: nextCents,
        locationName: acc.name,
        currency: acc.currency,
      });
    } else {
      budgetMut.mutate({ rowId: row.id, platform, monthlyCents: nextCents, patchAccount: false });
    }
  };

  // Pre-P2b-3 §2: per-platform column groups. When a row's effective
  // runsMeta/runsGoogle is false (Account flag false + no override forces
  // it on), every column in the corresponding set renders dimmed and
  // non-interactive. The set deliberately omits Ad Accounts URLs (kept
  // readable so AMs can still navigate) and the Ready / Status / Notes /
  // Actions / Local Assets columns (cross-platform or row-level).
  const META_PLATFORM_COLUMNS = new Set<ColumnKey>([
    // Pre-P2b-3 polish round 5 follow-up: per-row Meta ad type override.
    // Dims with the rest of the Meta columns when runsMeta=false.
    'metaType',
    'metaMonthly',
    'metaIncrease',
    'metaDaily',
    // Ready (Fix 2): the Ready-to-Build group dims with the rest of the
    // platform's columns. The server's ready-gate validator also rejects
    // metaReadyToBuild=true when the effective Meta flag is false, so
    // even a stale client can't sneak a ready flip past it.
    'metaReady',
    'metaBuilt',
    'metaLocalBuilt',
    'metaOrganicBuilt',
    'metaLive',
    'metaPostCheck',
    'assetsMeta',
  ]);
  const GOOGLE_PLATFORM_COLUMNS = new Set<ColumnKey>([
    'googleMonthly',
    'googleIncrease',
    'googleDaily',
    'googleReady',
    'googleBuilt',
    'googleScheduled',
    'googlePostCheck',
    'assetsGoogle',
  ]);

  // Per-column cell renderer. Kept as a single switch for readability -- the
  // column-driven loop picks the right case without branching on coordinates.
  //
  // Opt-out rows (P2b-2 §6.7.3): all cells except Location, Ad Accounts,
  // Notes, Status, and Actions render as a subdued em-dash and are non-
  // interactive. Location + Ad Accounts stay readable (the AM needs to
  // identify the row, and Ad Account URLs are account-level references, not
  // row-authored data -- §6.7.4). Status renders the 'Opted Out' pill via
  // the existing status pipeline.
  const OPT_OUT_VISIBLE_COLUMNS = new Set<ColumnKey>([
    'location',
    'adAccountMeta',
    'adAccountGoogle',
    'status',
    'rowNotes',
    'actions',
  ]);

  const renderCell = (colKey: ColumnKey, row: AccountCampaign): ReactNode => {
    const acc = accountsById.get(row.accountId);
    if (!acc) return null;
    const currency = acc.currency;
    const campaign = row.campaignId ? campaignsById.get(row.campaignId) ?? null : null;
    const am = row.assignedAmId ? usersById.get(row.assignedAmId) ?? null : null;

    if (row.rowType === 'opt_out' && !OPT_OUT_VISIBLE_COLUMNS.has(colKey)) {
      return <span className="text-slate-400">—</span>;
    }

    // Pre-P2b-3 §2 + §3: platform-disabled cells render dimmed and
    // non-interactive. Effective flag layers Account-level + per-row
    // override.
    //
    // Polish (Fix 3): we no longer use td-level `pointer-events-none` to
    // block clicks — that also blocked hover, which broke the native
    // `title` tooltip on disabled cells. Instead we keep the wrapping
    // `<td>` interactive and pass `disabled` on each input / button /
    // select / checkbox in the switch below. The browser still respects
    // the disabled attribute (no clicks fire); hover events propagate to
    // the `<td>` and its `title` shows the explanatory text.
    // The campaign's channels are the third layer (Meta only / Google only).
    const rowChannels = row.campaignId ? campaignsById.get(row.campaignId)?.channels : undefined;
    const metaActive = effectiveRunsMeta(acc.runsMeta, row.metaEnabled, rowChannels);
    const googleActive = effectiveRunsGoogle(acc.runsGoogle, row.googleEnabled, rowChannels);
    const platformDisabled =
      (!metaActive && META_PLATFORM_COLUMNS.has(colKey)) ||
      (!googleActive && GOOGLE_PLATFORM_COLUMNS.has(colKey));
    // Convenience: any case in the switch below that previously took
    // `disabled={archived}` should now take `disabled={inputDisabled}` so
    // platform-off rows render their input as disabled too.
    const inputDisabled = archived || platformDisabled;
    // Daily and the URL-style asset cells don't have a single disable-able
    // input; they stay on the bespoke render branches.
    if (platformDisabled) {
      if (colKey === 'metaDaily' || colKey === 'googleDaily') {
        return (
          <DailyBudgetCell
            row={row}
            platform={colKey === 'metaDaily' ? 'meta' : 'google'}
            currency={currency}
            disabled
          />
        );
      }
      if (colKey === 'assetsMeta' || colKey === 'assetsGoogle') {
        return <span className="text-slate-300 select-none">—</span>;
      }
      // Local / Organic Built have their own em-dash branch when the row
      // has no files; with the platform off, render the same dash so the
      // disabled checkbox is replaced (a disabled checkbox in an already-
      // muted cell looks louder than a dash).
      if (colKey === 'metaLocalBuilt' || colKey === 'metaOrganicBuilt') {
        return <span className="text-slate-300 select-none">—</span>;
      }
      // For every other platform-gated case (Monthly, Increase, Built,
      // Live, Post-check, Scheduled, Ready post-Fix-2), fall through to
      // the switch below — the case will pass `disabled={inputDisabled}`
      // so the underlying input renders its native disabled state.
    }

    switch (colKey) {
      case 'location': {
        const profile = locationProfileById.get(acc.id);
        const suburb = profile?.franchiseSuburb?.trim();
        const label = suburb && suburb.length > 0 ? suburb : acc.name;
        const group = resolveGroupTagForRow(row, accountsById);
        return (
          <div className="flex items-center gap-2">
            {!row.primary && <span className="text-slate-400">↳</span>}
            <Link
              to={`/accounts/${acc.id}`}
              className="font-medium hover:underline"
              title={acc.name}
            >
              {label}
            </Link>
            <GroupBadge name={group.name} />
          </div>
        );
      }

      case 'assignedAm':
        return (
          <>
            <select
              value={row.assignedAmId ?? ''}
              disabled={archived}
              onChange={(e) => patchField(row.id, { assignedAmId: e.target.value || null })}
              className="rounded border border-slate-200 px-1 py-0.5 text-xs max-w-[140px]"
            >
              <option value="">— None —</option>
              {users.map((u) => (
                <option key={u.uid} value={u.uid}>
                  {formatUserDisplayName(u)}
                </option>
              ))}
            </select>
            {am === null && row.assignedAmId && (
              <p className="text-xs text-slate-400 mt-0.5">(unknown user)</p>
            )}
          </>
        );

      case 'campaign': {
        // Pencil icon navigates to the row editor (P2b-2 §7.1.2). Disabled
        // states mirror the Actions menu's Edit Campaign entry — the two
        // entry points must gate identically so there's no "pencil works
        // but menu doesn't" inconsistency. Tooltip copy matches spec.
        const editDisabledReason = rowEditDisabledReason(row);
        // Pre-P2b-3 polish round 3 (Fix 1 + Fix 3): unified
        // modification dot. True when the row has Campaign content
        // drift (existing diff helper) OR date drift (new
        // creation-time anchors). Helper handles the opt-out /
        // unassigned bypass internally and degrades gracefully when
        // the Campaign isn't in hand (transient lookup miss). Renders
        // independently of editDisabledReason — even a (hypothetically)
        // disabled pencil should still show the drift signal so the
        // AM knows to investigate.
        const showModificationDot = hasRowModifications(
          row,
          row.campaignId ? campaignsById.get(row.campaignId) ?? null : null,
        );
        // Pre-P2b-3 §5.6: per-row dropdown shows Campaigns visible to this
        // row's location AND status=active. Opt-out / Alternate Campaigns
        // continue to show with their existing labels (Q7). The row's
        // currently-assigned Campaign is always preserved in the list,
        // even if visibility narrowed it out — otherwise the AM sees a
        // blank dropdown for an assigned row.
        const visibleCampaigns = campaigns.filter((c) => {
          if (c.id === row.campaignId) return true;
          if (c.status !== 'active') return false;
          return isCampaignVisibleToLocation(c, row.accountId, accountsById);
        });
        return (
          <div className="flex items-center gap-1">
            <CampaignCellDropdown
              row={row}
              currentCampaign={campaign}
              options={visibleCampaigns}
              disabled={archived}
              onSelect={(val) => {
                if (val !== row.campaignId) {
                  if (
                    !window.confirm(
                      "Assigning a different Campaign will replace this row's content snapshot. Continue?",
                    )
                  ) {
                    return;
                  }
                }
                patchField(row.id, { campaignId: val });
              }}
            />
            <span className="relative inline-block">
              <button
                type="button"
                disabled={editDisabledReason !== null}
                onClick={() => navigate(`/rollouts/${rollout.id}/rows/${row.id}`)}
                title={editDisabledReason ?? 'Edit row content'}
                aria-label="Edit row content"
                className="text-xs text-slate-400 hover:text-slate-700 disabled:opacity-30 disabled:cursor-not-allowed px-1"
              >
                ✎
              </button>
              {showModificationDot && (
                // Small orange dot (8px) positioned at the top-right of
                // the pencil. Pure indicator — no tooltip / interaction;
                // the existing pencil tooltip stays unchanged. Bump to
                // 10px later if AMs report missing it.
                <span
                  aria-hidden="true"
                  className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-orange-500 pointer-events-none"
                />
              )}
            </span>
          </div>
        );
      }

      // Multi Lead Form spec §3.1 + §3.2. Cell handles unassigned /
      // single-form / multi-form / orphan states internally; picking a
      // form fires the inline patch with leadFormId and the server re-
      // snapshots row.leadForm from the assigned Campaign.
      case 'leadForm':
        return (
          <LeadFormCell
            row={row}
            campaign={campaign}
            disabled={archived}
            busy={patchMut.isPending}
            onChange={(leadFormId) => patchField(row.id, { leadFormId })}
            ariaLabel={`Lead form for ${acc.name}`}
          />
        );
      case 'adAccountMeta':
        return urlCell(acc.fbAdAccountUrl);
      case 'adAccountGoogle':
        return urlCell(acc.googleAdsUrl);

      // Pre-P2b-3 polish round 4: per-cell date drift dot. Mirrors the
      // pencil-icon dot styling (8px orange, top-right) but localises
      // the signal to the specific date that drifted from its
      // creation-time *Original anchor. Helpers gate internally on
      // opt-out (no dot) and legacy-undefined-Original (no dot until
      // server backfill catches up); unassigned rows still surface
      // the dot per spec.
      case 'startDate':
        return (
          <span className="relative inline-block">
            <DatePickerCell
              valueIso={row.startDate}
              disabled={archived}
              onSubmit={(iso) => patchFieldAsync(row.id, { startDate: iso })}
              ariaLabel={`Start date for ${acc.name}`}
            />
            {hasStartDateDrift(row) && (
              <span
                aria-hidden="true"
                className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-orange-500 pointer-events-none"
              />
            )}
          </span>
        );
      case 'endDate':
        return (
          <span className="relative inline-block">
            <DatePickerCell
              valueIso={row.endDate}
              disabled={archived}
              onSubmit={(iso) => patchFieldAsync(row.id, { endDate: iso })}
              ariaLabel={`End date for ${acc.name}`}
            />
            {hasEndDateDrift(row) && (
              <span
                aria-hidden="true"
                className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-orange-500 pointer-events-none"
              />
            )}
          </span>
        );

      case 'metaType': {
        // Pre-P2b-3 polish round 5 follow-up: per-row Meta ad type
        // override. The Campaign provides the locked / 'both' decision;
        // the location profile provides the default the row inherits
        // from when its own override is null. effectiveMetaAdType
        // resolves the chain at render time.
        if (!campaign) {
          // Unassigned rows: nothing to author against. Show a muted
          // dash so the column scans cleanly.
          return <span className="text-slate-400">—</span>;
        }
        const profile = locationProfileById.get(acc.id) ?? null;
        return (
          <MetaAdTypeCell
            campaignMetaAdType={campaign.metaAdCopy.metaAdType}
            rowOverride={row.metaAdType}
            locationDefault={profile?.defaultMetaAdType ?? null}
            busy={patchMut.isPending}
            disabled={inputDisabled}
            onChange={(next) => patchField(row.id, { metaAdType: next })}
          />
        );
      }
      case 'metaMonthly':
        // Editable on every row, not just primary (P2b polish post-Chunk-C).
        // Secondaries can hold their own Monthly so AMs can split a
        // location's retainer across two campaigns; the drift modal stays
        // primary-only because drift is "row Monthly ≠ Account retainer"
        // and only the primary row is anchored to the retainer.
        return (
          <MonthlyBudgetInput
            valueCents={row.metaMonthlyBudget}
            disabled={inputDisabled}
            ariaLabel={`Meta monthly budget for ${acc.name}`}
            onCommit={(cents) => onMonthlyCommit(row, 'meta', cents)}
          />
        );
      case 'metaIncrease':
        return (
          <IncreaseInput
            valueCents={row.metaCampaignIncrease}
            disabled={inputDisabled}
            ariaLabel={`Meta campaign increase for ${acc.name}`}
            onCommit={(cents) => patchField(row.id, { metaCampaignIncrease: cents })}
          />
        );
      case 'metaDaily':
        return (
          <DailyBudgetCell
            row={row}
            platform="meta"
            currency={currency}
            modified={isBudgetModified(row, 'meta')}
            accountMonthlyBudget={acc.metaMasterMonthlyBudget}
          />
        );

      case 'googleMonthly':
        // See `metaMonthly` above — same secondary-row editability rules.
        return (
          <MonthlyBudgetInput
            valueCents={row.googleMonthlyBudget}
            disabled={inputDisabled}
            ariaLabel={`Google monthly budget for ${acc.name}`}
            onCommit={(cents) => onMonthlyCommit(row, 'google', cents)}
          />
        );
      case 'googleIncrease':
        return (
          <IncreaseInput
            valueCents={row.googleCampaignIncrease}
            disabled={inputDisabled}
            ariaLabel={`Google campaign increase for ${acc.name}`}
            onCommit={(cents) => patchField(row.id, { googleCampaignIncrease: cents })}
          />
        );
      case 'googleDaily':
        return (
          <DailyBudgetCell
            row={row}
            platform="google"
            currency={currency}
            modified={isBudgetModified(row, 'google')}
            accountMonthlyBudget={acc.googleMasterMonthlyBudget}
          />
        );

      case 'metaReady':
        return (
          <input
            type="checkbox"
            checked={row.metaReadyToBuild}
            disabled={inputDisabled}
            onChange={(e) => patchField(row.id, { metaReadyToBuild: e.target.checked })}
          />
        );
      case 'googleReady':
        return (
          <input
            type="checkbox"
            checked={row.googleReadyToBuild}
            disabled={inputDisabled}
            onChange={(e) => patchField(row.id, { googleReadyToBuild: e.target.checked })}
          />
        );

      case 'status': {
        const status = computeAccountCampaignStatus(row as RowForStatus, acc.status);
        return (
          <span className={`text-xs px-2 py-0.5 rounded-full ${statusPillClass(status)}`}>
            {status}
          </span>
        );
      }

      // Execution fields: inline-editable checkboxes from the master grid
      // (same pattern as Meta Ready / Google Ready). No validation beyond
      // the archived-rollout guard -- AMs tick/untick freely; last-write-wins.
      // The Meta/Google platform grids (P2b-3) also write these fields; last
      // write wins.
      //
      // Meta Local Built is gated on localAssetsUrl: with no url there's no
      // local variant to build, so the cell renders an em-dash and the
      // checkbox is suppressed entirely (toggling would be meaningless).
      case 'metaBuilt':
        return (
          <input
            type="checkbox"
            checked={row.metaBuilt}
            disabled={inputDisabled}
            onChange={(e) => patchField(row.id, { metaBuilt: e.target.checked })}
          />
        );
      case 'metaLocalBuilt':
        return shouldRenderMetaLocalBuiltCell(row) ? (
          <input
            type="checkbox"
            checked={row.metaLocalBuilt}
            disabled={inputDisabled}
            onChange={(e) => patchField(row.id, { metaLocalBuilt: e.target.checked })}
          />
        ) : (
          <span className="text-slate-300" title="No Local files yet">—</span>
        );
      case 'metaOrganicBuilt':
        return shouldRenderMetaOrganicBuiltCell(row) ? (
          <input
            type="checkbox"
            checked={row.metaOrganicBuilt}
            disabled={inputDisabled}
            onChange={(e) => patchField(row.id, { metaOrganicBuilt: e.target.checked })}
          />
        ) : (
          <span className="text-slate-300" title="No Organic files yet">—</span>
        );
      case 'metaLive':
        return (
          <input
            type="checkbox"
            checked={row.metaTurnedOn}
            disabled={inputDisabled}
            onChange={(e) => patchField(row.id, { metaTurnedOn: e.target.checked })}
          />
        );
      case 'metaPostCheck':
        return (
          <input
            type="checkbox"
            checked={row.metaPostRolloutCheck}
            disabled={inputDisabled}
            onChange={(e) => patchField(row.id, { metaPostRolloutCheck: e.target.checked })}
          />
        );
      case 'googleBuilt':
        return (
          <input
            type="checkbox"
            checked={row.googleBuilt}
            disabled={inputDisabled}
            onChange={(e) => patchField(row.id, { googleBuilt: e.target.checked })}
          />
        );
      case 'googleScheduled':
        return (
          <input
            type="checkbox"
            checked={row.googleScheduled}
            disabled={inputDisabled}
            onChange={(e) => patchField(row.id, { googleScheduled: e.target.checked })}
          />
        );
      case 'googlePostCheck':
        return (
          <input
            type="checkbox"
            checked={row.googlePostRolloutCheck}
            disabled={inputDisabled}
            onChange={(e) => patchField(row.id, { googlePostRolloutCheck: e.target.checked })}
          />
        );

      case 'assetsMeta':
        return urlCell(row.metaAssetsFolderUrl ?? campaign?.metaAssetsFolderUrl ?? null);
      case 'assetsGoogle':
        return urlCell(row.googleAssetsFolderUrl ?? campaign?.googleAssetsFolderUrl ?? null);

      // OrganicAssets spec §5.3 / §6.1: Local + Organic asset cells. The
      // View link is blue when the folder has files (hasLocalFiles /
      // hasOrganicFiles), amber when the folder exists but is empty. URL
      // editing stays a quick prompt; the row action menu's "Create Meta
      // Asset Folder" mints both leaves when neither exists.
      case 'assetsLocal':
        return assetFolderCell({
          url: row.localAssetsUrl,
          hasFiles: row.hasLocalFiles ?? false,
          archived,
          promptLabel: 'Local assets URL',
          onSet: (next) => patchField(row.id, { localAssetsUrl: next }),
        });
      case 'assetsOrganic':
        return assetFolderCell({
          url: row.organicAssetsUrl ?? null,
          hasFiles: row.hasOrganicFiles ?? false,
          archived,
          promptLabel: 'Organic assets URL',
          onSet: (next) => patchField(row.id, { organicAssetsUrl: next }),
        });

      case 'rowNotes': {
        // Prefer the Location-column label convention (franchiseSuburb with
        // fallback to account name) so the popover header reads naturally.
        const profile = locationProfileById.get(acc.id);
        const suburb = profile?.franchiseSuburb?.trim();
        const label = suburb && suburb.length > 0 ? suburb : acc.name;
        return (
          <NotesCell
            value={row.rowNotes}
            locationLabel={label}
            archived={archived}
            onSave={(next) => patchFieldAsync(row.id, { rowNotes: next })}
          />
        );
      }

      case 'actions': {
        const locationLabel = (() => {
          const profile = locationProfileById.get(acc.id);
          const suburb = profile?.franchiseSuburb?.trim();
          return suburb && suburb.length > 0 ? suburb : acc.name;
        })();
        return (
          <RowActionsMenu
            row={row}
            brandOptOut={brandOptOut}
            archived={archived}
            onEditCampaign={() =>
              navigate(`/rollouts/${rollout.id}/rows/${row.id}`)
            }
            editDisabledReason={rowEditDisabledReason(row)}
            onDuplicate={() =>
              setDuplicateTarget({ rowId: row.id, locationName: locationLabel })
            }
            onHistory={() =>
              setHistoryTarget({ rowId: row.id, locationName: locationLabel })
            }
            onMarkOptOut={() => setOptOutTarget({ rowId: row.id, locationName: locationLabel })}
            onReverseOptOut={() => reverseOptOutMut.mutate(row.id)}
            onDeleteSecondary={() => {
              if (
                !window.confirm(
                  `Delete this secondary row for ${locationLabel}? This cannot be undone.`,
                )
              )
                return;
              deleteSecondaryMut.mutate(row.id);
            }}
            onGeneratePreview={() =>
              setPreviewTarget({
                rowId: row.id,
                locationName: locationLabel,
                existingLinkId: row.latestPreviewLinkId ?? null,
              })
            }
            onCreateFolders={() => createRowFoldersMut.mutate(row.id)}
            createFoldersDisabledReason={
              archived
                ? 'Rollout is archived (read-only).'
                : !brandDriveConfigured
                  ? "Configure the brand's Drive parent folder first (Brand settings)."
                  : isUrl(row.localAssetsUrl) && isUrl(row.organicAssetsUrl)
                    ? 'Folders already exist for this row.'
                    : null
            }
          />
        );
      }
    }
  };

  const totalColumns = header.orderedColumns.length + 1; // +1 for the checkbox col

  return (
    <>
      <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
        <table className="min-w-max text-sm">
          <thead className="text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-2 py-2 bg-slate-50 border-b border-slate-200" rowSpan={3}>
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={(e) =>
                    onToggleAll(
                      e.target.checked,
                      visibleRows.map((r) => r.id),
                    )
                  }
                  aria-label="Select all visible rows"
                />
              </th>
              {header.row1.map((cell) => (
                <th
                  key={`r1-${cell.topGroup}`}
                  colSpan={cell.colSpan}
                  rowSpan={cell.rowSpan}
                  className={`px-2 py-2 font-semibold text-center border-b border-slate-200 ${
                    cell.shaded ? 'bg-slate-100' : 'bg-slate-50'
                  }`}
                >
                  <span className="inline-flex items-center">
                    {cell.label}
                    {onVisibleColumnsChange && (
                      <GroupCogMenu
                        topGroup={cell.topGroup}
                        columnOrder={effectiveOrder}
                        visibleColumns={effectiveVisible}
                        onVisibleChange={onVisibleColumnsChange}
                      />
                    )}
                  </span>
                </th>
              ))}
            </tr>
            <tr>
              {header.row2.map((cell) => (
                <th
                  key={`r2-${cell.subGroup}`}
                  colSpan={cell.colSpan}
                  className={`px-2 py-1.5 font-medium text-center border-b border-slate-200 ${
                    cell.shaded ? 'bg-slate-100' : 'bg-slate-50'
                  }`}
                >
                  {cell.label}
                </th>
              ))}
            </tr>
            <tr>
              {header.row3.map((cell) => (
                <th
                  key={`r3-${cell.column}`}
                  className={`px-2 py-2 font-medium whitespace-nowrap text-left border-b border-slate-200 ${
                    cell.shaded ? 'bg-slate-100' : 'bg-slate-50'
                  }`}
                >
                  {cell.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleRows.length === 0 ? (
              <tr>
                <td
                  colSpan={totalColumns}
                  className="px-4 py-6 text-center text-slate-500 text-sm"
                >
                  No rows match the current filters.
                </td>
              </tr>
            ) : (
              visibleRows.map((row) => {
                const acc = accountsById.get(row.accountId);
                if (!acc) return null;
                const isOptOut = row.rowType === 'opt_out';
                // Opt-out shading (§6.7.3) takes precedence over the secondary-
                // row zebra so the row reads as "deliberately parked" rather
                // than "extra campaign on this location".
                const rowBg = isOptOut
                  ? 'bg-slate-200'
                  : !row.primary
                    ? 'bg-slate-50/40'
                    : '';
                return (
                  <tr
                    key={row.id}
                    className={`border-t border-slate-100 hover:bg-slate-100 ${rowBg}`}
                  >
                    <td className="px-2 py-1.5 align-top">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(row.id)}
                        onChange={(e) => onToggleSelect(row.id, e.target.checked)}
                        aria-label={`Select ${acc.name}`}
                      />
                    </td>
                    {header.orderedColumns.map((colKey) => {
                      const shade = cellShadeClass(headerTopGroupByColumn(header, colKey));
                      // Pre-P2b-3 §2 dim class. Originally bg-slate-100 +
                      // pointer-events-none, but the latter blocks hover and
                      // breaks the native title tooltip. Polish (Fix 3):
                      // keep the visual treatment, drop pointer-events-none,
                      // and rely on the `disabled` attribute on each input
                      // / checkbox / button inside renderCell to suppress
                      // clicks. Hover events propagate to the td so the
                      // title tooltip fires correctly.
                      const accForRow = accountsById.get(row.accountId);
                      const rowChannelsForDim = row.campaignId
                        ? campaignsById.get(row.campaignId)?.channels
                        : undefined;
                      const isMetaCol = META_PLATFORM_COLUMNS.has(colKey);
                      const isGoogleCol = GOOGLE_PLATFORM_COLUMNS.has(colKey);
                      const platformOff =
                        (isMetaCol &&
                          !effectiveRunsMeta(
                            accForRow?.runsMeta,
                            row.metaEnabled,
                            rowChannelsForDim,
                          )) ||
                        (isGoogleCol &&
                          !effectiveRunsGoogle(
                            accForRow?.runsGoogle,
                            row.googleEnabled,
                            rowChannelsForDim,
                          ));
                      const dimClass = platformOff
                        ? 'bg-slate-100 text-slate-400'
                        : '';
                      const dimTitle = platformOff
                        ? isMetaCol
                          ? campaignIncludesChannel(rowChannelsForDim, 'meta')
                            ? "Account doesn't run Meta"
                            : "This Campaign doesn't include Meta"
                          : campaignIncludesChannel(rowChannelsForDim, 'google')
                            ? "Account doesn't run Google"
                            : "This Campaign doesn't include Google"
                        : undefined;
                      return (
                        <td
                          key={colKey}
                          className={`px-2 py-1.5 align-top whitespace-nowrap ${shade} ${dimClass}`}
                          title={dimTitle}
                        >
                          {renderCell(colKey, row)}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {optOutTarget && (
        <OptOutConfirmModal
          locationName={optOutTarget.locationName}
          busy={optOutMut.isPending}
          onCancel={() => {
            if (optOutMut.isPending) return;
            setOptOutTarget(null);
          }}
          onConfirm={() => {
            optOutMut.mutate(optOutTarget.rowId, {
              onSettled: () => setOptOutTarget(null),
            });
          }}
        />
      )}

      {duplicateTarget && (
        <DuplicateRowModal
          locationName={duplicateTarget.locationName}
          busy={duplicateMut.isPending}
          onCancel={() => {
            if (duplicateMut.isPending) return;
            setDuplicateTarget(null);
          }}
          onConfirm={() => {
            duplicateMut.mutate(duplicateTarget.rowId, {
              onSettled: () => setDuplicateTarget(null),
            });
          }}
        />
      )}

      {historyTarget && (
        <RowHistoryModal
          rolloutId={rollout.id}
          rowId={historyTarget.rowId}
          locationName={historyTarget.locationName}
          onClose={() => setHistoryTarget(null)}
        />
      )}

      {previewTarget && (
        <GeneratePreviewModal
          rolloutId={rollout.id}
          rowId={previewTarget.rowId}
          locationName={previewTarget.locationName}
          existingLinkId={previewTarget.existingLinkId}
          onClose={() => setPreviewTarget(null)}
        />
      )}

      {drift && (
        <BudgetDriftModal
          platform={drift.platform}
          locationName={drift.locationName}
          rolloutName={rollout.name}
          fromCents={drift.fromCents}
          toCents={drift.toCents}
          currency={drift.currency}
          onPermanent={() => {
            budgetMut.mutate({
              rowId: drift.rowId,
              platform: drift.platform,
              monthlyCents: drift.toCents,
              patchAccount: true,
            });
            setDrift(null);
          }}
          onOneTime={() => {
            budgetMut.mutate({
              rowId: drift.rowId,
              platform: drift.platform,
              monthlyCents: drift.toCents,
              patchAccount: false,
            });
            setDrift(null);
          }}
          onCancel={() => setDrift(null)}
        />
      )}
    </>
  );
}

function isUrl(url: string | null | undefined): boolean {
  return typeof url === 'string' && url.trim().length > 0;
}

function urlCell(url: string | null): React.ReactNode {
  return url ? (
    <a href={url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline text-xs">
      View →
    </a>
  ) : (
    <span className="text-slate-300">—</span>
  );
}

// Local / Organic asset folder cell (OrganicAssets spec §5.3). View link
// colour reflects file presence: blue = folder has files, amber = folder
// exists but empty. No folder URL → "+ Add" (manual paste); the row action
// menu's "Create Meta Asset Folder" is the auto-create path.
function assetFolderCell(opts: {
  url: string | null;
  hasFiles: boolean;
  archived: boolean;
  promptLabel: string;
  onSet: (next: string | null) => void;
}): React.ReactNode {
  const { url, hasFiles, archived, promptLabel, onSet } = opts;
  if (url) {
    return (
      <div className="flex items-center gap-1">
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className={`hover:underline text-xs ${hasFiles ? 'text-blue-600' : 'text-amber-600'}`}
          title={hasFiles ? undefined : 'Folder exists but has no files yet'}
        >
          View →
        </a>
        <button
          type="button"
          disabled={archived}
          onClick={() => {
            const next = window.prompt(promptLabel, url ?? '');
            if (next === null) return;
            onSet(next || null);
          }}
          className="text-xs text-slate-400 hover:text-slate-700"
          title="Edit"
        >
          ✎
        </button>
      </div>
    );
  }
  return (
    <button
      type="button"
      disabled={archived}
      onClick={() => {
        const next = window.prompt(promptLabel);
        if (!next) return;
        onSet(next);
      }}
      className="text-xs text-slate-500 hover:text-slate-800 underline"
    >
      + Add
    </button>
  );
}

// Small reverse lookup: given a ColumnKey, find its topGroup via the header's
// row3 cells (already computed). Keeps cellShadeClass consumers out of
// columns.ts's COLUMN_BY_KEY map without a second import.
function headerTopGroupByColumn(
  header: ReturnType<typeof buildHeader>,
  colKey: ColumnKey,
): ReturnType<typeof buildHeader>['row3'][number]['topGroup'] {
  const cell = header.row3.find((c) => c.column === colKey);
  // Defensive: if somehow the column isn't in the header (shouldn't happen
  // because renderCell is driven by header.orderedColumns), fall back to a
  // neutral group.
  return cell?.topGroup ?? 'identification';
}

// Unused import/alias reference silenced for readability.
void centsToDisplay;
